<div class="vendor-container">
  <div class="vendor-header">
    <h2>{{ vendorId ? (isEditMode ? 'Edit Vendor' : 'View Vendor') : 'Create New Vendor' }}</h2>
    <div class="header-actions" *ngIf="vendorId && !isEditMode">
      <button mat-raised-button color="accent" (click)="enterEditMode()" type="button">
        <mat-icon>edit</mat-icon> Edit
      </button>
    </div>
  </div>

  <div *ngIf="loading" class="loading-message">
    <p>Loading vendor data...</p>
  </div>

  <div *ngIf="error" class="error-message">
    <p>{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadVendor(vendorId!)" *ngIf="vendorId">Retry</button>
  </div>

  <form *ngIf="!loading" [formGroup]="vendorForm" (ngSubmit)="onSubmit()" class="vendor-form">
    <!-- Basic Information Section -->
    <h3>Vendor Information</h3>
    
    <div class="form-row">
      <mat-form-field appearance="outline" class="form-field-full">
        <mat-label>Vendor Name</mat-label>
        <input matInput formControlName="name" placeholder="Enter vendor name" [readonly]="!isEditMode">
        <mat-error *ngIf="vendorForm.get('name')?.hasError('required')">
          Vendor name is required
        </mat-error>
      </mat-form-field>
    </div>

    <!-- Integration Information Section -->
    <div style="display: none;">
      <h3>Integration Information</h3>
      <div class="form-row">
        <mat-form-field appearance="outline" class="form-field">
          <mat-label>QuickBooks Vendor ID</mat-label>
          <input matInput type="number" formControlName="qboVendorId" placeholder="Enter QuickBooks ID">
        </mat-form-field>
      </div>
    </div>

    <!-- Address Section -->
    <div class="section-header">
      <h3>Address Information</h3>
      <button mat-mini-fab color="primary" type="button" (click)="addAddress()" matTooltip="Add Address" *ngIf="isEditMode">
        <mat-icon>add</mat-icon>
      </button>
    </div>

    <mat-accordion formArrayName="addresses">
      <mat-expansion-panel *ngFor="let address of addresses.controls; let i = index">
        <mat-expansion-panel-header>
          <mat-panel-title>
            {{ getAddressTitle(i) }}
          </mat-panel-title>
          <mat-panel-description>
            <button mat-icon-button color="warn" type="button" (click)="removeAddress(i); $event.stopPropagation()" matTooltip="Remove Address" *ngIf="isEditMode">
              <mat-icon>delete</mat-icon>
            </button>
          </mat-panel-description>
        </mat-expansion-panel-header>
        <app-address [formControlName]="i" [readonly]="!isEditMode"></app-address>
      </mat-expansion-panel>
    </mat-accordion>
    <div class="empty-message" *ngIf="addresses.length === 0">
      <span *ngIf="!isEditMode">No addresses found.</span>
      <span *ngIf="isEditMode">No addresses added. Click + to add an address.</span>
    </div>

    <!-- Contact Section -->
    <div class="section-header">
      <h3>Contact Information</h3>
      <button mat-mini-fab color="primary" type="button" (click)="addContact()" matTooltip="Add Contact" *ngIf="isEditMode">
        <mat-icon>add</mat-icon>
      </button>
    </div>

    <mat-accordion formArrayName="contacts">
      <mat-expansion-panel *ngFor="let contact of contacts.controls; let i = index">
        <mat-expansion-panel-header>
          <mat-panel-title>
            {{ getContactTitle(i) }}
          </mat-panel-title>
          <mat-panel-description>
            <button mat-icon-button color="warn" type="button" (click)="removeContact(i); $event.stopPropagation()" matTooltip="Remove Contact" *ngIf="isEditMode">
              <mat-icon>delete</mat-icon>
            </button>
          </mat-panel-description>
        </mat-expansion-panel-header>
        <app-contact [formControlName]="i" [readonly]="!isEditMode"></app-contact>
      </mat-expansion-panel>
    </mat-accordion>
    <div class="empty-message" *ngIf="contacts.length === 0">
      <span *ngIf="!isEditMode">No contacts found.</span>
      <span *ngIf="isEditMode">No contacts added. Click + to add a contact.</span>
    </div>

    <!-- Accounts Section -->
    <div class="section-header">
      <h3>Accounts</h3>
      <button mat-mini-fab color="primary" type="button" (click)="addAccount()" matTooltip="Add Account" *ngIf="isEditMode">
        <mat-icon>add</mat-icon>
      </button>
    </div>

    <mat-accordion formArrayName="accounts">
      <mat-expansion-panel *ngFor="let account of accounts.controls; let i = index" [formGroupName]="i">
        <mat-expansion-panel-header>
          <mat-panel-title>
            {{ getAccountTitle(i) }}
          </mat-panel-title>
          <mat-panel-description>
            <button mat-icon-button color="warn" type="button" (click)="removeAccount(i); $event.stopPropagation()" matTooltip="Remove Account" *ngIf="isEditMode">
              <mat-icon>delete</mat-icon>
            </button>
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="form-row">
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Account Name</mat-label>
            <input matInput formControlName="name" placeholder="Enter account name" [readonly]="!isEditMode">
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Account Number</mat-label>
            <input matInput formControlName="accountNumber" placeholder="Enter account number" [readonly]="!isEditMode">
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Provider</mat-label>
            <input matInput formControlName="provider" placeholder="e.g. FedEx, UPS, DHL" [readonly]="!isEditMode">
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field">
            <mat-label>Account Type</mat-label>
            <mat-select formControlName="type" [disabled]="!isEditMode">
              <mat-option *ngFor="let t of accountTypes" [value]="t">{{ t }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Notes</mat-label>
            <textarea matInput formControlName="notes" rows="2" placeholder="Optional notes" [readonly]="!isEditMode"></textarea>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-checkbox formControlName="isActive" [disabled]="!isEditMode">Active</mat-checkbox>
        </div>

      </mat-expansion-panel>
    </mat-accordion>
    <div class="empty-message" *ngIf="accounts.length === 0">
      <span *ngIf="!isEditMode">No accounts found.</span>
      <span *ngIf="isEditMode">No accounts added. Click + to add an account.</span>
    </div>

    <div class="form-actions" *ngIf="isEditMode">
      <button mat-raised-button color="primary" type="submit" [disabled]="!vendorForm.valid">
        {{ vendorId ? 'Update' : 'Create' }}
      </button>
      <button mat-raised-button type="button" (click)="onCancel()">
        Cancel
      </button>
    </div>
  </form>
</div>
